'use client';
import { useEffect, useMemo, useState } from 'react';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import Link from 'next/link';
import { Card } from '../../components/ui/card';
import { Skeleton } from '../../components/Skeleton';
import { EmptyState } from '../../components/EmptyState';

type ScenarioIndex = { slug: string; title: string; path: string; keywords?: string[] };

export default function ScenariosPage() {
  const [all, setAll] = useState<ScenarioIndex[]>([]);
  const [q, setQ] = useState('');
  const [tag, setTag] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/data/scenarios/index.json')
      .then((r) => {
        if (!r.ok) throw new Error('Failed to load scenarios');
        return r.json();
      })
      .then((data) => {
        setAll(data);
        setError(null);
      })
      .catch((err) => {
        console.error('Failed to load scenarios:', err);
        setError('Failed to load scenarios. Please try refreshing the page.');
        setAll([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const keywords = useMemo(() => {
    const set = new Set<string>();
    all.forEach((s) => (s.keywords ?? []).forEach((k) => set.add(k)));
    return Array.from(set).sort();
  }, [all]);

  const filtered = useMemo(() => {
    return all.filter((s) => {
      const matchesQ = s.title.toLowerCase().includes(q.toLowerCase());
      const matchesTag = !tag || (s.keywords ?? []).includes(tag);
      return matchesQ && matchesTag;
    });
  }, [all, q, tag]);

  if (loading) {
    return (
      <div className="space-y-6 md:space-y-8 w-full">
        <div className="text-center space-y-3">
          <Skeleton variant="text" className="h-10 w-64 mx-auto" />
          <Skeleton variant="text" className="h-6 w-96 mx-auto" />
        </div>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <Skeleton variant="rectangular" className="h-10 md:w-80 w-full" />
          <div className="flex flex-wrap gap-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rectangular" className="h-6 w-16" />
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} variant="rectangular" className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4 sm:space-y-6 md:space-y-8 w-full">
        <div className="text-center space-y-2 sm:space-y-3">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold break-words">
            Practice Scenarios
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl mx-auto px-3 sm:px-4 break-words">
            Test your skills in realistic situations: crisis communication, Socratic dialogue, and
            stakeholder management.
          </p>
        </div>
        <Card className="p-8">
          <EmptyState
            title="Failed to Load Scenarios"
            message={error}
            cta={{ href: '/', label: 'Back to Home' }}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 md:space-y-8 w-full">
      <div className="text-center space-y-2 sm:space-y-3">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold break-words">
          Practice Scenarios
        </h1>
        <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl mx-auto px-3 sm:px-4 break-words">
          Test your skills in realistic situations: crisis communication, Socratic dialogue, and
          stakeholder management.
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:gap-4 md:flex-row md:items-center md:justify-between">
        <Input
          placeholder="Search scenarios..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="md:max-w-sm w-full"
        />
        <div className="flex flex-wrap gap-2 justify-center md:justify-end">
          <Badge
            variant={!tag ? 'default' : 'secondary'}
            onClick={() => setTag(null)}
            className="cursor-pointer hover:bg-primary/80 transition-colors"
          >
            All
          </Badge>
          {keywords.map((k) => (
            <Badge
              key={k}
              variant={tag === k ? 'default' : 'secondary'}
              className="cursor-pointer hover:bg-primary/80 transition-colors"
              onClick={() => setTag(k)}
            >
              {k}
            </Badge>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="p-4 sm:p-6 md:p-8 lg:p-12 text-center">
          <div className="max-w-md mx-auto space-y-4">
            {q ? (
              <>
                <div className="text-4xl mb-4">🔍</div>
                <h2 className="text-2xl font-semibold">No scenarios found</h2>
                <p className="text-muted-foreground">
                  Try a different search term or clear your filters to see all available scenarios.
                </p>
                <div className="pt-4">
                  <button
                    onClick={() => setQ('')}
                    className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors font-medium"
                  >
                    Clear Search
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="text-4xl mb-4">🎯</div>
                <h2 className="text-2xl font-semibold">Practice Scenarios Coming Soon</h2>
                <p className="text-muted-foreground">
                  We&apos;re building pressure-tested scenarios for crisis communication, ethical
                  decision-making, and stakeholder reasoning.
                </p>
                <div className="pt-4 space-y-3">
                  <p className="text-sm font-medium text-foreground">
                    Start building your foundation:
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link
                      href="/ritual"
                      className="inline-flex items-center justify-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-medium"
                    >
                      Start Daily Ritual
                    </Link>
                    <Link
                      href="/modules"
                      className="inline-flex items-center justify-center px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors font-medium"
                    >
                      Explore Modules
                    </Link>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-6">
                  More scenarios shipping weekly.{' '}
                  <Link href="/roadmap" className="text-primary hover:underline">
                    See roadmap
                  </Link>
                </p>
              </>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
          {filtered.map((s) => (
            <Link key={s.slug} href={`/scenarios/${s.slug}`}>
              <Card className="p-6 hover:shadow-lg transition-all h-full group">
                <div className="text-xl font-semibold break-words mb-2 group-hover:text-primary transition-colors">
                  {s.title}
                </div>
                {s.keywords && s.keywords.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {s.keywords.slice(0, 3).map((k) => (
                      <Badge key={k} variant="secondary" className="text-xs">
                        {k}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="text-sm text-primary font-medium mt-4">Practice scenario →</div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
